import heic2any from "heic2any";

// Configuração do Cloudinary
export const CLOUDINARY_CONFIG = {
  cloudName: "dmc3nymnh",
  uploadPreset: "fechamento_semanal" // Vamos criar isso no Cloudinary
};

// Função para converter HEIC/HEIF para JPEG
const convertHeicToJpeg = async (file) => {
  try {
    console.log(`🔄 Converting HEIC image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

    const convertedBlob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.9 // High quality conversion
    });

    // heic2any pode retornar um blob ou array de blobs (para múltiplas imagens)
    const finalBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

    // Criar novo File object com nome .jpg
    const newFileName = file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg");
    const convertedFile = new File([finalBlob], newFileName, {
      type: "image/jpeg",
      lastModified: Date.now()
    });

    console.log(`✅ HEIC converted: ${file.name} → ${newFileName} (${(convertedFile.size / 1024 / 1024).toFixed(2)}MB)`);
    return convertedFile;
  } catch (error) {
    console.error(`❌ Error converting HEIC: ${file.name}`, error);
    throw new Error(`Failed to convert HEIC image: ${error.message}`);
  }
};

// Função para comprimir vídeo (simplificada - apenas avisa mas permite upload)
const compressVideo = async (file) => {
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

  // Se vídeo já é <= 50MB, retornar original
  if (file.size <= MAX_VIDEO_SIZE) {
    console.log(`✓ Vídeo OK: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    return file;
  }

  console.log(`⚠️ Vídeo grande: ${(file.size / 1024 / 1024).toFixed(2)}MB - Upload pode demorar...`);
  // Nota: Compressão de vídeo client-side é muito pesada e pode travar o browser
  // Deixamos o Cloudinary fazer a compressão no servidor usando transformações automáticas
  return file;
};

// Função para comprimir imagem até um tamanho alvo
const compressImage = async (file) => {
  // Primeiro, verificar se é HEIC e converter
  const isHeic = file.name.toLowerCase().endsWith('.heic') ||
                 file.name.toLowerCase().endsWith('.heif') ||
                 file.type === 'image/heic' ||
                 file.type === 'image/heif';

  let fileToProcess = file;

  if (isHeic) {
    try {
      fileToProcess = await convertHeicToJpeg(file);
    } catch (error) {
      console.error('Error converting HEIC, will try to upload original:', error);
      // Se conversão falhar, tenta upload do original
      fileToProcess = file;
    }
  }

  return new Promise((resolve, reject) => {
    // Se não for imagem, processar vídeo
    if (!fileToProcess.type.startsWith('image/')) {
      if (fileToProcess.type.startsWith('video/')) {
        compressVideo(fileToProcess).then(resolve).catch(reject);
      } else {
        // Outro tipo de arquivo
        resolve(fileToProcess);
      }
      return;
    }

    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

    // Se imagem já é menor que 10MB, não comprimir
    if (fileToProcess.size <= MAX_IMAGE_SIZE) {
      console.log(`✓ Imagem OK: ${(fileToProcess.size / 1024 / 1024).toFixed(2)}MB (não precisa comprimir)`);
      resolve(fileToProcess);
      return;
    }

    console.log(`⚠️ Imagem grande: ${(fileToProcess.size / 1024 / 1024).toFixed(2)}MB - Comprimindo até 10MB...`);

    const reader = new FileReader();
    reader.readAsDataURL(fileToProcess);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Manter dimensões originais
        canvas.width = img.width;
        canvas.height = img.height;

        // Desenhar imagem no canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Compressão iterativa até atingir <= 10MB
        const tryCompress = async (quality) => {
          return new Promise((resolveBlob) => {
            canvas.toBlob(
              (blob) => {
                resolveBlob(blob);
              },
              'image/jpeg',
              quality
            );
          });
        };

        // Tentar diferentes níveis de qualidade até atingir <= 10MB
        let quality = 0.9;
        let blob = null;

        while (quality >= 0.1) {
          blob = await tryCompress(quality);

          if (blob && blob.size <= MAX_IMAGE_SIZE) {
            // Conseguiu comprimir até <= 10MB
            const compressedFile = new File([blob], fileToProcess.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            console.log(`✅ Imagem comprimida: ${(fileToProcess.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (qualidade ${Math.round(quality * 100)}%)`);
            resolve(compressedFile);
            return;
          }

          // Reduzir qualidade e tentar novamente
          quality -= 0.1;
        }

        // Se mesmo com qualidade mínima ainda é > 10MB, usar a última tentativa
        if (blob) {
          const compressedFile = new File([blob], fileToProcess.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          console.log(`⚠️ Imagem comprimida ao máximo: ${(fileToProcess.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (qualidade mínima)`);
          resolve(compressedFile);
        } else {
          reject(new Error('Erro ao comprimir imagem'));
        }
      };

      img.onerror = () => {
        reject(new Error('Erro ao carregar imagem'));
      };
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };
  });
};

// Função para upload de arquivo
export const uploadToCloudinary = async (file) => {
  // Comprimir imagem/validar vídeo antes do upload
  const fileToUpload = await compressImage(file);

  const formData = new FormData();
  formData.append("file", fileToUpload);
  formData.append("upload_preset", CLOUDINARY_CONFIG.uploadPreset);
  formData.append("cloud_name", CLOUDINARY_CONFIG.cloudName);

  // Aceitar todos os tipos de arquivo (Cloudinary vai processar)
  formData.append("resource_type", "auto");

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/upload`,
      {
        method: "POST",
        body: formData
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "Erro ao fazer upload");
    }

    const data = await response.json();
    console.log("Upload bem-sucedido:", data);
    return {
      url: data.secure_url,
      publicId: data.public_id,
      resourceType: data.resource_type,
      fileName: file.name
    };
  } catch (error) {
    console.error("Erro no upload:", error);
    throw error;
  }
};
